import Cookies from 'universal-cookie';
import { useSelector } from 'store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useUser from 'hooks/useUser';

const cookies = new Cookies();

/**
 * Returns a hash code from a string
 * @param  {String} string The string to hash.
 * @return {Number}    A 32bit integer
 * @see http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 */
const hashCode = (string: string): string => {
  let hash = 0;

  for (let i = 0, len = string.length; i < len; i++) {
    let chr = string.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }

  return hash.toString(16);
};

const useCookieBanner = (areAnalyticsAccepted: boolean) => {
  const isLoggedIn = useSelector((store) => store.auth.isLoggedIn);
  const [isVisible, setIsVisible] = useState(false);
  const {
    email,
    id: user_id,
    organization: { id: organization_id, name },
  } = useUser();

  /**
   * Calculate cookie key for logged-in user
   */
  const cookieKey = useMemo(() => {
    return 'cookie-consent-v2-' + hashCode(email);
  }, [email]);

  const startAnalytics = useCallback(() => {
    window.dataLayer?.push({
      event: 'start-analytics',
      email,
      user_id,
      organization_id,
      organization_name: name,
    });
  }, [email, user_id, organization_id, name]);

  /**
   * Accept cookies - set it and change state to false
   */
  const onAccept = useCallback(() => {
    cookies.set(cookieKey, { cookies: true, analytics: areAnalyticsAccepted });
    if (areAnalyticsAccepted) {
      startAnalytics();
    }
    setIsVisible(false);
  }, [cookieKey, areAnalyticsAccepted]);

  useEffect(() => {
    const parsedCookies = cookies.get(cookieKey, { doNotParse: false });
    if (isLoggedIn && (!parsedCookies || parsedCookies.cookies === false)) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    if (isLoggedIn && parsedCookies && parsedCookies.analytics) {
      startAnalytics();
    }
  }, [isLoggedIn, cookieKey]);

  return {
    isVisible,
    onAccept,
  } as const;
};

export default useCookieBanner;
